import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { 
  FileText, 
  Download, 
  PieChart,
  Users,
  DollarSign,
  RotateCcw,
  FileSpreadsheet,
  FileImage
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useExport } from '../hooks/useExport';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChartsReport = () => {
  const { exportToExcel, exportToPDF, exportToCSV, exporting } = useExport();
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState({
    employeesByCompany: [],
    totalsByCompany: []
  });

  useEffect(() => {
    fetchChartData();
  }, []);

  const fetchChartData = async () => {
    try {
      setLoading(true);
      
      // Fetch data for both charts
      const [employeesResponse, totalsResponse] = await Promise.all([
        axios.get(`${API}/dashboard/count-by-company`),
        axios.get(`${API}/dashboard/total-by-company`)
      ]);

      setChartData({
        employeesByCompany: employeesResponse.data,
        totalsByCompany: totalsResponse.data
      });
    } catch (error) {
      console.error('Erro ao carregar dados dos gráficos:', error);
      toast.error('Erro ao carregar dados dos gráficos');
    } finally {
      setLoading(false);
    }
  };

  // Generate colors for pie charts
  const generateColors = (count) => {
    const colors = [
      'rgba(59, 130, 246, 0.8)',   // Blue
      'rgba(16, 185, 129, 0.8)',   // Green
      'rgba(245, 158, 11, 0.8)',   // Yellow
      'rgba(239, 68, 68, 0.8)',    // Red
      'rgba(139, 92, 246, 0.8)',   // Purple
      'rgba(236, 72, 153, 0.8)',   // Pink
      'rgba(20, 184, 166, 0.8)',   // Teal
      'rgba(251, 146, 60, 0.8)',   // Orange
    ];
    
    return colors.slice(0, count);
  };

  const generateBorderColors = (count) => {
    const borderColors = [
      'rgba(59, 130, 246, 1)',
      'rgba(16, 185, 129, 1)',
      'rgba(245, 158, 11, 1)',
      'rgba(239, 68, 68, 1)',
      'rgba(139, 92, 246, 1)',
      'rgba(236, 72, 153, 1)',
      'rgba(20, 184, 166, 1)',
      'rgba(251, 146, 60, 1)',
    ];
    
    return borderColors.slice(0, count);
  };

  // Prepare data for employees by company chart
  const employeesChartData = {
    labels: chartData.employeesByCompany.map(item => item.company),
    datasets: [
      {
        label: 'Funcionários',
        data: chartData.employeesByCompany.map(item => item.count),
        backgroundColor: generateColors(chartData.employeesByCompany.length),
        borderColor: generateBorderColors(chartData.employeesByCompany.length),
        borderWidth: 2,
      },
    ],
  };

  // Prepare data for totals by company chart
  const totalsChartData = {
    labels: chartData.totalsByCompany.map(item => item.company),
    datasets: [
      {
        label: 'Total da Folha',
        data: chartData.totalsByCompany.map(item => item.total),
        backgroundColor: generateColors(chartData.totalsByCompany.length),
        borderColor: generateBorderColors(chartData.totalsByCompany.length),
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            
            if (context.datasetIndex === 0 && chartData.employeesByCompany.length > 0) {
              return `${label}: ${value} funcionários (${percentage}%)`;
            } else {
              const formatted = new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
              }).format(value);
              return `${label}: ${formatted} (${percentage}%)`;
            }
          }
        }
      }
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const handleExportCSV = () => {
    if (chartData.employeesByCompany.length === 0 && chartData.totalsByCompany.length === 0) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    const filename = `relatorio_graficos_gridprofile_${new Date().toISOString().split('T')[0]}`;
    
    // Combine both datasets
    const combinedData = [];
    
    // Add employees data
    chartData.employeesByCompany.forEach(item => {
      const totalItem = chartData.totalsByCompany.find(t => t.company === item.company);
      combinedData.push({
        empresa: item.company,
        quantidade_funcionarios: item.count,
        total_folha: totalItem ? totalItem.total : 0
      });
    });

    const headers = ['Empresa', 'Quantidade de Funcionários', 'Total da Folha'];
    exportToCSV(combinedData, filename, headers);
  };

  const handleExportExcel = () => {
    if (chartData.employeesByCompany.length === 0 && chartData.totalsByCompany.length === 0) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    const filename = `relatorio_graficos_gridprofile_${new Date().toISOString().split('T')[0]}`;
    
    // Combine both datasets
    const combinedData = [];
    
    chartData.employeesByCompany.forEach(item => {
      const totalItem = chartData.totalsByCompany.find(t => t.company === item.company);
      combinedData.push({
        'Empresa': item.company,
        'Quantidade de Funcionários': item.count,
        'Total da Folha': totalItem ? totalItem.total : 0
      });
    });

    exportToExcel(combinedData, filename, 'Relatório de Gráficos');
  };

  const handleExportPDF = () => {
    if (chartData.employeesByCompany.length === 0 && chartData.totalsByCompany.length === 0) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    const filename = `relatorio_graficos_gridprofile_${new Date().toISOString().split('T')[0]}`;
    
    // Combine both datasets
    const combinedData = [];
    
    chartData.employeesByCompany.forEach(item => {
      const totalItem = chartData.totalsByCompany.find(t => t.company === item.company);
      combinedData.push({
        empresa: item.company,
        quantidade_funcionarios: item.count,
        total_folha: totalItem ? totalItem.total : 0
      });
    });

    const columns = [
      { header: 'Empresa', dataKey: 'empresa' },
      { header: 'Colaboradores', dataKey: 'quantidade_funcionarios' },
      { header: 'Total da Folha', dataKey: 'total_folha' }
    ];

    exportToPDF(combinedData, filename, 'Relatório de Gráficos', columns);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loading-spinner w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Relatório de Gráficos</h1>
          <p className="text-slate-600 mt-1">
            Visualização gráfica dos dados de funcionários e folha de pagamento
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            onClick={fetchChartData}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <RotateCcw className="h-4 w-4" />
            <span>Atualizar</span>
          </Button>
          
          {(chartData.employeesByCompany.length > 0 || chartData.totalsByCompany.length > 0) && (
            <>
              <Button
                onClick={handleExportCSV}
                disabled={exporting}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>CSV</span>
              </Button>
              
              <Button
                onClick={handleExportExcel}
                disabled={exporting}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <FileSpreadsheet className="h-4 w-4" />
                <span>Excel</span>
              </Button>
              
              <Button
                onClick={handleExportPDF}
                disabled={exporting}
                className="btn-primary flex items-center space-x-2"
              >
                <FileImage className="h-4 w-4" />
                <span>PDF</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Header with company logo */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-200">
            <div className="flex items-center space-x-3">
              <div>
                <h2 className="text-lg font-bold text-slate-800">GridProfile</h2>
                <p className="text-sm text-slate-600">Sistema de Folha de Pagamento</p>
              </div>
            </div>
            <div className="text-right text-sm text-slate-600">
              <p>Relatório gerado em:</p>
              <p className="font-medium">{new Date().toLocaleDateString('pt-BR')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Employees by Company Chart */}
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Users className="h-5 w-5 text-blue-600" />
              <span>Funcionários por Empresa</span>
            </CardTitle>
            <CardDescription>
              Distribuição da quantidade de funcionários por empresa
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              {chartData.employeesByCompany.length > 0 ? (
                <Pie data={employeesChartData} options={chartOptions} />
              ) : (
                <div className="flex items-center justify-center h-full text-slate-500">
                  <div className="text-center">
                    <PieChart className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Nenhum dado disponível</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Totals by Company Chart */}
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              <span>Total da Folha por Empresa</span>
            </CardTitle>
            <CardDescription>
              Distribuição do valor total da folha de pagamento por empresa
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              {chartData.totalsByCompany.length > 0 ? (
                <Pie data={totalsChartData} options={chartOptions} />
              ) : (
                <div className="flex items-center justify-center h-full text-slate-500">
                  <div className="text-center">
                    <PieChart className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Nenhum dado disponível</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Resumo Estatístico</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-slate-700 mb-3">Funcionários por Empresa</h4>
              <div className="space-y-2">
                {chartData.employeesByCompany.map((item, index) => (
                  <div key={index} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                    <span className="font-medium">{item.company}</span>
                    <span className="text-blue-600 font-bold">{item.count} funcionários</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-slate-700 mb-3">Total da Folha por Empresa</h4>
              <div className="space-y-2">
                {chartData.totalsByCompany.map((item, index) => (
                  <div key={index} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                    <span className="font-medium">{item.company}</span>
                    <span className="text-green-600 font-bold">{formatCurrency(item.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChartsReport;